import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { DOCUMENT_FORM_NAME_DEBT_SCHEDULE } from "@datanac/datanac-api-toolkit";
import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit";
import { AddCircleOutline } from "@mui/icons-material";
import { Button, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { getBudgetYears } from "components/Budget/helpers";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import {
  MASK_NUMERIC,
  MASK_PERCENTAGE,
  StatefulControlledTextField,
} from "components/Core/StatefulControlledTextField";
import { TilleyDatePicker } from "components/Core/TilleyDatePicker";
import {
  DEBT_SCHEDULE_CATEGORIES,
  DEBT_SCHEDULE_PAYMENT_FREQUENCIES,
  DEBT_SCHEDULE_PURPOSE_LIST,
} from "components/Financial/helpers";
import { FinancialIcon } from "components/Icons/Icons";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import { useChatAssistantAttachments } from "../../../contexts/useChatAssistantAttachments";
import { DEBT_SCHEDULE_DAY_COUNT_CONVENTIONS } from "../helpers";
import useDebtScheduleAmortization from "../hooks/useDebtScheduleAmortization";
import { DebtScheduleAmortizationTable } from "./DebtScheduleAmortizationTable";
import { DebtServiceStackedBarChart } from "./DebtServiceStackedBarChart";

const DEBT_FIELDS_TO_SYNC = [
  "payment_amount",
  "interest_paid",
  "principal_paid",
  "principal_total",
  "liability_current",
  "liability_intermediate",
  "liability_longterm",
  "interest_accrued",
];

export default function DebtScheduleWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { addAttachments } = useChatAssistantAttachments();

  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();
  const { component: formStatusComponent, actions: formStatusActions, state: formStatusState } = useFormStatus();

  const [currentYear] = useState(globalState.crop_year);
  const [isLoadingDebtSchedule, setIsLoadingDebtSchedule] = useState(true);

  const { equipment_id, debt_schedule_id } = useParams();

  const [state, dispatch] = useReducer(propUpdateReducer, {
    is_active: true,
    year: currentYear,
  });

  // Initialize amortization hook (must be after state is declared)
  const {
    amortizationSchedule,
    calculatedFields,
    generateAmortizationSchedule,
    hasExistingData,
    reloadSchedule,
    isLoading: isAmortizationLoading,
  } = useDebtScheduleAmortization(state?.id, state?.year, globalState);

  useEffect(() => {
    if (calculatedFields) {
      const updates = {};
      let hasChanges = false;

      DEBT_FIELDS_TO_SYNC.forEach((key) => {
        const calculatedValue = parseFloat(calculatedFields[key]);
        const currentStateValue = parseFloat(state[key] || 0);

        if (Math.abs(calculatedValue - currentStateValue) > 0.001) {
          updates[key] = calculatedValue;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        formStatusActions.setIsFormDirty(true, false);
        dispatch({
          type: "updateMany",
          payload: updates,
        });
      }
    }
  }, [calculatedFields]);

  const api = useMemo(() => {
    return new ApiHelper({
      baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
      config: {},
      sessionManager: _sessionManager,
      _instance: null,
    });
  }, [_sessionManager]);

  const isValid = useCallback(() => {
    if (!state?.id || !state?.year || !state?.creditor || !state?.purpose || !state?.category) {
      return false;
    }

    if (state?.inception_date === "" || state?.maturity_date === "") {
      return false;
    }

    return true;
  }, [state]);

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  useEffect(() => {
    if (String(debt_schedule_id) === "0") {
      dispatch({
        type: "update",
        payload: { key: "id", value: uuidv4() },
      });
      setIsLoadingDebtSchedule(false);
    } else {
      loadDebtScheduleData();
      dispatch({
        type: "update",
        payload: { key: "id", value: debt_schedule_id },
      });
    }
  }, [globalState?.producer_token, debt_schedule_id]);
  const [equipment, setEquipment] = useState({});
  useEffect(() => {
    if (equipment_id) {
      loadEquipmentData();
    }
  }, [equipment_id]);
  const loadEquipmentData = async () => {
    try {
      const equipmentList = await UsersApiHelper.selectEquipmentInventory(
        {
          producer_token: globalState.producer_token,
          year: currentYear,
          is_active: true,
          id: equipment_id,
        },
        globalState
      );
      const foundEquipment = Array.isArray(equipmentList)
        ? equipmentList.find((eq) => eq.id === equipment_id) || null
        : null;
      setEquipment(foundEquipment);
    } catch (err) {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || "Error loading equipment", {
        preventDuplicate: true,
        variant: "error",
      });
    }
  };
  const loadDebtScheduleData = async () => {
    try {
      const _filter = {
        id: debt_schedule_id,
        producer_token: globalState?.producer_token,
        is_active: true,
      };

      const debtSchedules = await api.callPost("debtSchedule/Search", _filter, globalState);
      if (debtSchedules?.length) {
        dispatch({
          type: "updateMany",
          payload: debtSchedules[0],
        });
      }
      setIsLoadingDebtSchedule(false);
    } catch (err) {
      setIsLoadingDebtSchedule(false);
      handleError(err);
    }
  };

  useEffect(() => {
    const updateDebtSchedule = async () => {
      try {
        formStatusActions.setIsFormDirty(true, true);

        const _documentsFilter = {
          year: state?.year ? state.year : currentYear,
          document_type_name: DOCUMENT_FORM_NAME_DEBT_SCHEDULE,
          is_active: true,
        };
        const _documents = await UsersApiHelper.users_selectObject("FinancialDocument", _documentsFilter, globalState);

        const _documentID = _documents?.length ? _documents[0].id : uuidv4();
        if (!_documents?.length) {
          const _financialDocument = {
            id: _documentID,
            year: state?.year ? state.year : currentYear,
            document_type_name: DOCUMENT_FORM_NAME_DEBT_SCHEDULE,
            is_active: true,
          };
          await UsersApiHelper.updateFinancialDocument(_financialDocument, globalState);
        }

        const _newValue = {
          ...state,
          document_id: state?.document_id ? state.document_id : _documentID,
          producer_token: globalState.producer_token,
        };

        await api.callPost(
          "debtSchedule",
          {
            DebtSchedule: [_newValue],
            document_id: state?.document_id ? state.document_id : _documentID,
            producer_token: globalState.producer_token,
          },
          globalState
        );
        formStatusActions.setIsSaving(false);

        if (debt_schedule_id === "0") {
          handleDebtScheduleSave();
        }
      } catch (err) {
        formStatusActions.setIsFormDirty(true, false);
        handleError(err);
      }
    };

    if (isValid() && globalState?.producer_token && formStatusState?.isFormDirty) {
      updateDebtSchedule();
    }
  }, [state]);

  const handleDebtScheduleSave = () => {
    navigate(`/financial/debt_schedule/${state?.id}`, { replace: true });
  };

  useEffect(() => {
    addAttachments([{ name: "Debt Schedule", content: state }]);
  }, [state]);

  // --- --- ---

  const handleChangeStateSelect = (event) => {
    if (event.target.value !== null) {
      formStatusActions.setIsFormDirty(true, false);

      dispatch({
        type: "update",
        payload: { key: event.target.name, value: event.target.value },
      });
    }
  };

  const handleChangeState = (event, value) => {
    value = event.target.value;

    if (state[event.target.name] !== value) {
      formStatusActions.setIsFormDirty(true, false);

      dispatch({
        type: "update",
        payload: { key: event.target.name, value: value },
      });
    }
  };

  const formMaxWidth = "1400px";
  // --- --- ---
  return (
    <>
      {equipment_id ? (
        <WorkspaceHeader
          title={state?.creditor ? state.creditor : "New Loan"}
          category={{
            title: "Financial Analysis",
            icon: <FinancialIcon />,
          }}
          breadCrumbs={[
            {
              title: "Equipment Inventory",
              path: "/financial/equipment",
            },
            {
              title: equipment?.name || "...",
              path: `/financial/equipment/${equipment_id}`,
            },
          ]}
        />
      ) : (
        <WorkspaceHeader
          title={state?.creditor ? state.creditor : "New Debt Schedule"}
          category={{
            title: "Financial Analysis",
            icon: <FinancialIcon />,
          }}
          breadCrumbs={[
            {
              title: "Debt Schedule",
              path: "/financial/debt_schedule",
            },
          ]}
        />
      )}
      <Box sx={{ width: "100%", maxWidth: formMaxWidth }}>
        <Stack direction="row" spacing={2} className="headerWithMenuButton headerWithMenuButtonAlignEnd">
          <Typography variant="h1">{equipment_id ? "Loan" : "Debt Schedule"}</Typography>
          {formStatusComponent}
        </Stack>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <SkeletonWrapper variant="textfield" sx={{ marginBottom: "20px" }} isLoading={isLoadingDebtSchedule}>
                <InputLabel id="selectYearLabel">Year</InputLabel>
                <Select
                  labelId="selectYearLabel"
                  value={state?.year || currentYear}
                  name={"year"}
                  onChange={handleChangeStateSelect}
                  label={"Year"}
                >
                  {getBudgetYears()?.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={currentItem}>
                        {currentItem}
                      </MenuItem>
                    );
                  })}
                </Select>
              </SkeletonWrapper>
            </FormControl>
          </Grid>
        </Grid>

        <Divider />

        <Stack direction="row" spacing={2} className="headerWithMenuButton headerWithMenuButtonAlignEnd">
          <Typography variant="h1">Note Details</Typography>
        </Stack>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Name"
                name="name"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={state.name || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Creditor"
                name="creditor"
                isRequired
                isLoading={isLoadingDebtSchedule}
                defaultValue={state.creditor || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} className="gridWithMargin">
            <FormControl fullWidth>
              <SkeletonWrapper variant="textfield" sx={{ marginBottom: "20px" }} isLoading={isLoadingDebtSchedule}>
                <InputLabel id="selectPurposeLabel">Purpose</InputLabel>
                <Select
                  labelId="selectPurposeLabel"
                  value={state?.purpose || ""}
                  name={"purpose"}
                  onChange={handleChangeStateSelect}
                  label={"Purpose"}
                  required={true}
                >
                  {DEBT_SCHEDULE_PURPOSE_LIST.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={currentItem}>
                        {currentItem}
                      </MenuItem>
                    );
                  })}
                </Select>
              </SkeletonWrapper>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} className="gridWithMargin">
            <FormControl fullWidth>
              <SkeletonWrapper variant="textfield" sx={{ marginBottom: "20px" }} isLoading={isLoadingDebtSchedule}>
                <InputLabel id="selectCategoryLabel">Category</InputLabel>
                <Select
                  labelId="selectCategoryLabel"
                  value={state?.category || ""}
                  name={"category"}
                  onChange={handleChangeStateSelect}
                  label={"Category"}
                  required={true}
                >
                  {DEBT_SCHEDULE_CATEGORIES.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={currentItem}>
                        {currentItem}
                      </MenuItem>
                    );
                  })}
                </Select>
              </SkeletonWrapper>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} className="claim_date_grid gridWithMargin">
            <TilleyDatePicker
              name="inception_date"
              label="Inception Date"
              value={state?.inception_date}
              onChange={handleChangeState}
              isLoading={isLoadingDebtSchedule}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} className="claim_date_grid gridWithMargin">
            <TilleyDatePicker
              name="maturity_date"
              label="Maturity Date"
              value={state?.maturity_date}
              onChange={handleChangeState}
              isLoading={isLoadingDebtSchedule}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Interest Rate"
                name="interest_rate"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={state.interest_rate || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_PERCENTAGE}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} className="gridWithMargin">
            <FormControl fullWidth>
              <SkeletonWrapper variant="textfield" sx={{ marginBottom: "20px" }} isLoading={isLoadingDebtSchedule}>
                <InputLabel id="selectPaymentFrequencyLabel">Payment Frequency</InputLabel>
                <Select
                  labelId="selectPaymentFrequencyLabel"
                  value={state?.payment_frequency || ""}
                  name={"payment_frequency"}
                  onChange={handleChangeStateSelect}
                  label={"Payment Frequency"}
                >
                  {DEBT_SCHEDULE_PAYMENT_FREQUENCIES.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={currentItem}>
                        {currentItem}
                      </MenuItem>
                    );
                  })}
                </Select>
              </SkeletonWrapper>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Payment Amount"
                name="payment_amount"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={calculatedFields?.payment_amount || state.payment_amount || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                disabled={true}
                helperText="(Auto-calculated)"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Original Amount"
                name="amount"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={state.amount || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Note"
                name="note"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={state.note || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 100 }}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Divider />

        <Stack direction="row" spacing={2} className="headerWithMenuButton headerWithMenuButtonAlignEnd">
          <Typography variant="h1">During {state?.year ? state?.year : currentYear}</Typography>
        </Stack>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Interest Paid"
                name="interest_paid"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={calculatedFields?.interest_paid || state.interest_paid || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                disabled={true}
                helperText="(Auto-calculated)"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Principal Paid"
                name="principal_paid"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={calculatedFields?.principal_paid || state.principal_paid || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                disabled={true}
                helperText="(Auto-calculated)"
              />
            </FormControl>
          </Grid>
        </Grid>

        <Divider />

        <Stack direction="row" spacing={2} className="headerWithMenuButton headerWithMenuButtonAlignEnd">
          <Typography variant="h1">Year End</Typography>
        </Stack>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Current Liability"
                name="liability_current"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={calculatedFields?.liability_current || state.liability_current || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                disabled={true}
                helperText="(Auto-calculated)"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Intermediate Liability"
                name="liability_intermediate"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={calculatedFields?.liability_intermediate || state.liability_intermediate || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                disabled={true}
                helperText="(Auto-calculated)"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Long-Term Liability"
                name="liability_longterm"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={calculatedFields?.liability_longterm || state.liability_longterm || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                disabled={true}
                helperText="(Auto-calculated)"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Accrued Interest"
                name="interest_accrued"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={calculatedFields?.interest_accrued || state.interest_accrued || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                disabled={true}
                helperText="(Auto-calculated)"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Total Principal"
                name="principal_total"
                isRequired={false}
                isLoading={isLoadingDebtSchedule}
                defaultValue={calculatedFields?.principal_total || state.principal_total || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                disabled={true}
                helperText="(Auto-calculated)"
              />
            </FormControl>
          </Grid>
        </Grid>

        <Divider />

        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Balloon Amount"
                name="balloon_amount"
                defaultValue={state?.balloon_amount || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Extra Principal Rate"
                name="extra_principal_rate"
                defaultValue={state?.extra_principal_rate || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_PERCENTAGE}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="selectDayCountConventionLabel">Day Count Convention</InputLabel>
              <Select
                labelId="selectDayCountConventionLabel"
                value={state?.day_count_convention || ""}
                name="day_count_convention"
                onChange={handleChangeStateSelect}
                label="Day Count Convention"
              >
                {DEBT_SCHEDULE_DAY_COUNT_CONVENTIONS.map((currentItem, index) => (
                  <MenuItem key={index} value={currentItem}>
                    {currentItem}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Interest Only Periods"
                name="interest_only_periods"
                defaultValue={state?.interest_only_periods || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={1}>
        <Grid item xl={6} sm={12}>
          {/* Debt Service Chart */}
          <DebtServiceStackedBarChart debt_schedule_id={state?.id} />
        </Grid>
      </Grid>

      <DebtScheduleAmortizationTable
        debt_schedule_id={state?.id}
        equipment_id={equipment_id}
        calculatedSchedule={amortizationSchedule}
        onScheduleChange={reloadSchedule}
      />
      <WorkspaceActionBar
        content={
          <>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  // Validate required fields
                  const errors = [];
                  if (!state.amount || parseFloat(state.amount) <= 0) {
                    errors.push("Original Amount");
                  }
                  if (state.interest_rate === undefined || state.interest_rate === null || state.interest_rate === "") {
                    errors.push("Interest Rate");
                  }
                  if (!state.inception_date) {
                    errors.push("Inception Date");
                  }
                  if (!state.maturity_date) {
                    errors.push("Maturity Date");
                  }
                  if (!state.payment_frequency) {
                    errors.push("Payment Frequency");
                  }

                  if (errors.length > 0) {
                    enqueueSnackbar(`Please complete all required fields: ${errors.join(", ")}`, {
                      variant: "error",
                      preventDuplicate: true,
                    });
                    return;
                  }

                  // Check if existing data exists
                  if (hasExistingData()) {
                    confirmDialogActions.confirm({
                      title: "Calculate Amortization Schedule?",
                      prompt: "Overwrite the existing schedule?",
                      promptDetail: "This action cannot be undone.",
                      callback: async () => {
                        try {
                          await generateAmortizationSchedule(state);
                          await loadDebtScheduleData(); // Reload to get updated payment_amount
                          enqueueSnackbar("Amortization schedule generated successfully", {
                            variant: "success",
                            preventDuplicate: true,
                          });
                        } catch (error) {
                          enqueueSnackbar(error.message || "Failed to generate amortization schedule", {
                            variant: "error",
                            preventDuplicate: true,
                          });
                        }
                      },
                    });
                  } else {
                    // No existing data, generate directly
                    await generateAmortizationSchedule(state);
                    await loadDebtScheduleData(); // Reload to get updated payment_amount
                    enqueueSnackbar("Amortization schedule generated successfully", {
                      variant: "success",
                      preventDuplicate: true,
                    });
                  }
                } catch (error) {
                  enqueueSnackbar(error.message || "Failed to generate amortization schedule", {
                    variant: "error",
                    preventDuplicate: true,
                  });
                }
              }}
              disabled={String(debt_schedule_id) === "0" || isAmortizationLoading}
            >
              <AddCircleOutline sx={{ mr: 1 }} />
              {isAmortizationLoading ? "Loading..." : "New Amortization"}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (equipment_id) {
                  navigate(`/financial/equipment/${equipment_id}/${state?.id}/restructure`);
                } else {
                  navigate(`/financial/debt_schedule/${state?.id}/restructure`);
                }
              }}
              disabled={String(debt_schedule_id) === "0"}
            >
              Restructure
            </Button>
          </>
        }
      />

      {confirmDialogComponent}
    </>
  );
}
